// frontend/src/notesClient.js
//
// Klient mot Aion Notes API för RecordingPanel (split fas 4).
//
// EHR-backenden byter in inloggad användare mot en Notes-token
// (POST /api/notes/token). Därefter pratar webbläsaren DIREKT med Notes —
// ljudet passerar aldrig EHR-backenden. Token cachas tills strax före utgång.

import apiClient from './apiClient';

let cachedSession = null; // { token, apiUrl, expiresAt }

export async function getNotesStatus() {
  const { data } = await apiClient.get('/api/notes/status');
  return data; // { enabled, reachable, notes_api_url }
}

async function getNotesSession() {
  if (cachedSession && Date.now() < cachedSession.expiresAt - 60000) {
    return cachedSession;
  }
  const { data } = await apiClient.post('/api/notes/token');
  cachedSession = {
    token: data.notes_token,
    apiUrl: data.notes_api_url,
    expiresAt: Date.now() + (data.expires_in || 3600) * 1000,
  };
  return cachedSession;
}

export function clearNotesSession() {
  cachedSession = null;
}

async function notesFetch(path, { method = 'GET', json, formData, stream = false, onData } = {}) {
  const { token, apiUrl } = await getNotesSession();
  const headers = { Authorization: `Bearer ${token}` };
  let body;
  if (json !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(json);
  } else if (formData !== undefined) {
    body = formData; // browsern sätter multipart-boundary själv
  }

  const response = await fetch(`${apiUrl}${path}`, { method, headers, body });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    const err = new Error(`Notes ${method} ${path} -> ${response.status}: ${text.slice(0, 200)}`);
    err.status = response.status;
    throw err;
  }

  if (stream) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let accumulated = '';
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value);
      accumulated += chunk;
      if (onData) onData(chunk, accumulated);
    }
    return accumulated;
  }
  return response.json();
}

/**
 * Starta en inspelningssession i Notes. Notes vet inget om patienten.
 * OBS: /recorder/sessions/start (inte bara /recorder/sessions) — start-
 * endpointen sätter status in_progress och väljer rätt whisper-backend
 * från Notes systeminställning (bara /recorder/sessions gav default
 * 'azure', vilket skickade chunks till nedstängda servrar).
 */
export async function createNotesRecordingSession(templateRef) {
  const payload = {};
  if (templateRef && /^\d+$/.test(String(templateRef))) {
    payload.template_id = parseInt(templateRef, 10);
  }
  const data = await notesFetch('/recorder/sessions/start', { method: 'POST', json: payload });
  return data.session_id;
}

/** Ladda upp en 30s wav-chunk. Returnerar ev. deltranskript. */
export async function uploadNotesChunk(sessionId, blob) {
  const formData = new FormData();
  formData.append('file', blob, `chunk-${Date.now()}.wav`);
  const data = await notesFetch(`/recorder/sessions/${sessionId}/upload_chunk`, {
    method: 'POST',
    formData,
  });
  const t = data.transcription;
  if (typeof t === 'string') return t;
  if (t && typeof t === 'object') return t.corrected_transcript || t.raw_transcript || '';
  return '';
}

/** Avsluta sessionen (elapsed i millisekunder). */
export async function completeNotesSession(sessionId, elapsedMs) {
  return notesFetch(`/recorder/sessions/${sessionId}/complete`, {
    method: 'POST',
    json: { elapsed_time: elapsedMs },
  });
}

/** Hämta sessionens läge (transkript, ev. färdig journal). */
export async function getNotesSessionData(sessionId) {
  return notesFetch(`/recorder/sessions/${sessionId}`);
}

/**
 * Generera journaltext från transkript enligt Notes-mall (streaming).
 * onData(chunk, accumulated) anropas löpande; hela texten returneras.
 */
export async function generateNotesJournal(text, templateRef, sessionId, onData) {
  return notesFetch('/api/chatgpt/generate_journal', {
    method: 'POST',
    json: {
      text,
      template_id: /^\d+$/.test(String(templateRef)) ? parseInt(templateRef, 10) : templateRef,
      session_id: sessionId,
    },
    stream: true,
    onData,
  });
}
